import type { CompanySectionKey } from "@shaarp/common";

/**
 * The sections each card of the company page is filled from: the card waits
 * on these only (`CompanySectionCard`), not on the whole run. The same card
 * reads the same sections on desktop, on mobile and in the side panel.
 */
export const COMPANY_CARD_SECTIONS = {
  activity: ["website", "maps", "discovery"],
  offerSummary: ["website", "maps", "discovery"],
  keyFigures: ["legal", "coresignal", "discovery"],
  summary: ["legal", "coresignal", "discovery"],
  market: ["coresignal"],
  // The registry's directors and the LinkedIn key executives the server
  // suggests from; a company without a domain gets it from `discovery`, which
  // `coresignal` waits on.
  employeesSuggested: ["legal", "coresignal"],
  events: ["legal", "coresignal"],
  headcountHistory: ["coresignal"],
  webTraffic: ["coresignal"],
  technologies: ["coresignal", "maps"],
  stock: ["coresignal"],
  funding: ["coresignal"],
  location: ["maps"],
  financialsSummary: ["legal"],
  financialsHistory: ["legal"],
  legalIdentity: ["legal"],
  establishments: ["legal"],
  relationshipGraph: ["legal", "coresignal"],
  reviews: ["reviews"],
  jobs: ["jobs"],
  posts: ["social"],
  press: ["coresignal"],
  employeeReviews: ["coresignal"],
  financialRatios: ["legal"],
  googleReviews: ["reviews", "maps"],
  competitors: ["coresignal"],
  talentFlow: ["coresignal"],
} as const satisfies Record<string, readonly CompanySectionKey[]>;

/** A card of the company page, by its key in {@link COMPANY_CARD_SECTIONS}. */
export type CompanyCardKey = keyof typeof COMPANY_CARD_SECTIONS;

/** The sections a group of cards (a tab) is filled from, each once. */
export function sectionsOfCards(
  cards: readonly CompanyCardKey[],
): CompanySectionKey[] {
  return [...new Set(cards.flatMap((card) => COMPANY_CARD_SECTIONS[card]))];
}

/**
 * The sections the desktop tabs that close on no data are filled from: read
 * by the tab bar, and by each tab for what it shows when one failed.
 */
export const COMPANY_TAB_SECTIONS = {
  news: sectionsOfCards([
    "posts",
    "reviews",
    "jobs",
    "press",
    "employeeReviews",
    "events",
    "googleReviews",
  ]),
  financials: sectionsOfCards([
    "financialsSummary",
    "financialRatios",
    "financialsHistory",
    "stock",
    "funding",
  ]),
  others: sectionsOfCards(["competitors", "talentFlow"]),
} as const satisfies Record<string, readonly CompanySectionKey[]>;
